import { Box, Container, Grid, Skeleton, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { ArrowLeft2 } from 'iconsax-react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { useSelector } from 'react-redux';

import { paths } from 'constants/endPoint';
import StoreCTX from 'provider/Store';
import { getSettingsLoading } from 'redux/settings/reducer';
import { getWidgets } from 'redux/stores/selectors';
import WidgetsLoading from 'screen/Stores/Skeleton/WidgetsLoading';
import { getOldBaseURL } from 'utility/baseURL';
import { decodeQueryStrings } from 'utility/decodeURL';

const Wallet = dynamic(() => import('screen/Stores/Components/Wallet'), { loading: () => <></> });
const SwiperKit = dynamic(() => import('components/common/Swiper'), {
	loading: () => <></>,
});
const LinkKit = dynamic(() => import('components/common/Link'), { loading: () => <></> });
const SquareStoreItem = dynamic(() => import('components/common/Boxes/SquareStoreItem'), {
	suspense: true,
});
const CategoriesBox = dynamic(() => import('components/common/Boxes/CategoriesBox'), {
	loading: () => <></>,
});
const ButtonKit = React.lazy(() =>
	import('components/kit/Button').then((module) => ({
		default: module.ButtonKit,
	})),
);

const useStyles = makeStyles((theme) => ({
	wrapperStoreTitle: {
		color: theme.palette.text.dark,
	},
	parentSlider: {
		width: '100%',
		position: 'relative',
		paddingRight: '1.6rem',
	},
	childrenSlider: {
		position: 'relative',
		width: '100%',
	},
}));

const WidgetsMobile = () => {
	const classes = useStyles();
	const widgetList = useSelector(getWidgets) || [];
	const { skeleton } = useSelector(getSettingsLoading);
	const theme = useTheme();
	const Router = useRouter();
	const { state } = React.useContext(StoreCTX);

	const handleMoveTo = (widget) => {
		const currentCategoryID = widget?.find((item) => item)?.CategoryID;

		Router.push({
			pathname: paths['/search'],
			query: decodeQueryStrings({
				'CurrentCategoryIds[0]': currentCategoryID,
				'app_keepa': state.app_keepa,
				sponsor_id: state.sponsor_id,
			}),
		});
	};

	return (
		<Grid container spacing={skeleton && !widgetList?.length ? 0 : 2}>
			{skeleton && widgetList?.length === 0 && <WidgetsLoading />}

			{!skeleton &&
				React.Children.toArray(
					widgetList.map((widget, index) => (
						<>
							{!state.app_keepa && index === 1 && (
								<Grid xs={12} item mt="8.8rem">
									<Container>
										<Wallet />
									</Container>
								</Grid>
							)}

							{widget.WidgetTypeName !== 'Banner' && (
								<Grid xs={12} item>
									<Container>
										<Box className="element-center" mt="5.6rem" flexDirection="column">
											<Typography variant="h4" fontWeight={700} className={classes.wrapperStoreTitle}>
												{widget.Title}
											</Typography>

											{['Category', 'SupplierCart'].includes(widget?.WidgetTypeName) && (
												<Typography
													variant="subtitle1"
													fontWeight={700}
													mt="0.8rem"
													color={theme.palette.text.secondary}
												>
													{widget.Subtitle}
												</Typography>
											)}
										</Box>
									</Container>
								</Grid>
							)}

							<Grid xs={12} item>
								<Container>
									<Grid container spacing={2}>
										{widget.Items.slice(0, 4).map((item) => (
											<>
												{widget.WidgetTypeName === 'SupplierCart' && (
													<Grid xs={6} item key={item.id}>
														<React.Suspense
															fallback={
																<Box width="100%">
																	<Skeleton
																		sx={{ bgcolor: theme.palette.primary[300], borderRadius: '1.2rem' }}
																		height={250}
																		width="100%"
																		variant="rounded"
																		animation="wave"
																	/>
																</Box>
															}
														>
															<Box
																component={LinkKit}
																href={`/stores/${item?.SupplierID || '/'}${
																	state.app_keepa ? `/?app_keepa=true&sponsor_id=${state.sponsor_id}` : ''
																}`}
															>
																<SquareStoreItem {...item} />
															</Box>
														</React.Suspense>
													</Grid>
												)}

												{widget.WidgetTypeName === 'Banner' && (
													<Grid key={widget.DefaultOffset} xs={12} item>
														<LinkKit
															href={
																item.Link?.includes('http')
																	? item.Link
																	: `${paths['/stores']}/${item.Link}${
																			state.app_keepa ? `/?app_keepa=true&sponsor_id=${state.sponsor_id}` : ''
																		}`
															}
															target={item.Link?.includes('http') ? '_blank' : '_self'}
														>
															<Box
																style={{
																	position: 'relative',
																	borderRadius: theme.shape.medium,
																	overflow: 'hidden',
																	marginTop: '0.8rem',
																	height: 'auto',
																}}
															>
																{/* eslint-disable-next-line @next/next/no-img-element */}
																<img
																	src={`${getOldBaseURL()}${item.Image}`}
																	loading="lazy"
																	alt={widget.Title}
																	placeholder="blur"
																	// objectFit="contain"
																	style={{ width: '100%', height: 'auto', borderRadius: theme.shape.medium }}
																	// blurDataURL={`data:image/svg+xml;base64,${shimmer(270, 155, 24)}`}
																/>
															</Box>
														</LinkKit>
													</Grid>
												)}
											</>
										))}
									</Grid>
								</Container>
							</Grid>

							{widget.WidgetTypeName === 'Category' && (
								<Grid xs={12} item>
									<Container disableGutters>
										<Box className={classes.parentSlider}>
											<SwiperKit
												data={widget?.Items || []}
												innerProps={{
													direction: 'horizontal',
													loop: true,
													slidesPerView: 3,
													spaceBetween: 120,
												}}
												proMode
												rootClassName={classes.childrenSlider}
											>
												{widget?.Items.map((category) => (
													<Box
														key={category.Name}
														onClick={() =>
															Router.push({
																pathname: paths['/search'],
																query: decodeQueryStrings({
																	'CurrentCategoryIds[0]': category?.CategoryID,
																	'app_keepa': state.app_keepa,
																	sponsor_id: state.sponsor_id,
																}),
															})
														}
													>
														<CategoriesBox
															key={category?.Name}
															image={category?.Images}
															Name={category?.Name}
															size=""
															Id={category?.CategoryID}
														/>
													</Box>
												))}
											</SwiperKit>
										</Box>
									</Container>
								</Grid>
							)}

							{!['Banner', 'Category'].includes(widget.WidgetTypeName) && widget?.DefaultOffset > 4 && (
								<Grid xs={12} item mt="2.4rem">
									<Container>
										<React.Suspense fallback={<></>}>
											<ButtonKit
												variant="outlined"
												fullWidth
												endLoading
												style={{
													padding: '1.55rem',
												}}
												endIcon={
													<ArrowLeft2
														size={18}
														color={theme.palette.text.hint}
														style={{ marginRight: '1.6rem' }}
													/>
												}
												onClick={() => handleMoveTo(widget.Items)}
											>
												<Typography variant="subtitle1" color={theme.palette.text.dark}>
													مشاهده فروشگاه بیشتر
												</Typography>
											</ButtonKit>
										</React.Suspense>
									</Container>
								</Grid>
							)}
						</>
					)),
				)}
		</Grid>
	);
};

export default WidgetsMobile;
